import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { packagesAPI } from '../../services/api';
import { Package, Calendar, Users, DollarSign, LogOut, Settings, Plus, List, TrendingUp, Image, Home, Info, Phone, HelpCircle, BarChart3, FileText, UserPlus, PieChart, Wallet, Gift, AlertTriangle, AlertCircle, Clock, ChevronRight, ChevronDown, ChevronUp, CheckCircle, Bell, Trash2, Edit2, Mail, Minimize2, Maximize2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPackages: 0,
    activePackages: 0,
    totalDates: 0,
    averagePrice: 0
  });
  const [loading, setLoading] = useState(true);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(true);
  const [availabilityAlerts, setAvailabilityAlerts] = useState([]);
  const [postTripReminders, setPostTripReminders] = useState([]);
  const [siteActivity, setSiteActivity] = useState({
    newReservations: 0,
    newCustomLeads: 0,
    totalReservations: 0,
    totalCustomLeads: 0,
    recentReservations: [],
    recentCustomLeads: []
  });
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null); // Para edição
  const [newReminder, setNewReminder] = useState({
    operation_id: '',
    title: '',
    description: '',
    due_date: '',
    priority: 'normal'
  });
  const [operations, setOperations] = useState([]);
  
  // Estados para minimizar seções
  const [expandReminders, setExpandReminders] = useState(() => {
    const saved = localStorage.getItem('dashboard_expandReminders');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [expandAvailability, setExpandAvailability] = useState(() => {
    const saved = localStorage.getItem('dashboard_expandAvailability');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Salvar estados no localStorage quando mudam
  useEffect(() => {
    localStorage.setItem('dashboard_expandReminders', JSON.stringify(expandReminders));
  }, [expandReminders]);
  
  useEffect(() => {
    localStorage.setItem('dashboard_expandAvailability', JSON.stringify(expandAvailability));
  }, [expandAvailability]);

  useEffect(() => {
    // Verificar autenticação
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }

    loadStats();
    loadPendingAlerts();
    loadPendingReminders();
    loadOperations();
    loadAvailabilityAlerts();
    loadPostTripReminders();
    loadSiteActivity();
  }, [navigate]);

  const loadPostTripReminders = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/operational-results/post-trip-reminders`);
      setPostTripReminders(r.data?.reminders || []);
    } catch (e) { setPostTripReminders([]); }
  };

  const markPostTripDone = async (operation_id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/operational-results/post-trip-reminders/mark`, { operation_id, completed: true });
      loadPostTripReminders();
    } catch (e) { alert('Erro ao marcar como concluído.'); }
  };

  const loadStats = async () => {
    try {
      const packages = await packagesAPI.getAll();
      const totalDates = packages.reduce((acc, pkg) => acc + (pkg.dates?.length || 0), 0);
      const avgPrice = packages.length > 0 
        ? packages.reduce((acc, pkg) => acc + pkg.price, 0) / packages.length 
        : 0;

      setStats({
        totalPackages: packages.length,
        activePackages: packages.filter(p => p.active !== false).length,
        totalDates,
        averagePrice: avgPrice
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingAlerts = async () => {
    try {
      setAlertsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/pending-expenses-alerts`);
      setPendingAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const loadPendingReminders = async () => {
    try {
      setRemindersLoading(true);
      
      // Primeiro, gerar lembretes automáticos para despesas pendentes
      try {
        await axios.post(`${BACKEND_URL}/api/operational-results/generate-auto-reminders`);
      } catch (e) {
        console.log('Auto-reminders check completed');
      }
      
      // Depois, carregar todos os lembretes pendentes
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/pending-reminders`);
      setPendingReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Erro ao carregar lembretes:', error);
    } finally {
      setRemindersLoading(false);
    }
  };

  const loadOperations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/operations`);
      setOperations(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    }
  };

  const loadAvailabilityAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/package-availability-alerts`);
      setAvailabilityAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erro ao carregar alertas de disponibilidade:', error);
    }
  };

  const loadSiteActivity = async () => {
    try {
      const [resRes, leadsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/lead-forms/responses-by-form/custom-packages-page`).catch(() => ({ data: [] }))
      ]);
      const reservations = Array.isArray(resRes.data) ? resRes.data : [];
      const customLeads = Array.isArray(leadsRes.data) ? leadsRes.data : [];

      const totalReservations = reservations.length;
      const totalCustomLeads = customLeads.length;

      const lastSeenRes = parseInt(localStorage.getItem('dashboard_lastSeenReservations') || '0', 10);
      const lastSeenLeads = parseInt(localStorage.getItem('dashboard_lastSeenCustomLeads') || '0', 10);

      const newReservations = Math.max(0, totalReservations - lastSeenRes);
      const newCustomLeads = Math.max(0, totalCustomLeads - lastSeenLeads);

      // Mais recentes (top 5 ordenado por created_at desc)
      const sortByCreatedAtDesc = (a, b) => {
        const at = new Date(a.created_at || 0).getTime();
        const bt = new Date(b.created_at || 0).getTime();
        return bt - at;
      };
      const recentReservations = [...reservations].sort(sortByCreatedAtDesc).slice(0, newReservations || 5);
      const recentCustomLeads = [...customLeads].sort(sortByCreatedAtDesc).slice(0, newCustomLeads || 5);

      setSiteActivity({
        newReservations,
        newCustomLeads,
        totalReservations,
        totalCustomLeads,
        recentReservations,
        recentCustomLeads
      });
    } catch (error) {
      console.error('Erro ao carregar atividade do site:', error);
    }
  };

  const markActivityAsSeen = () => {
    localStorage.setItem('dashboard_lastSeenReservations', String(siteActivity.totalReservations));
    localStorage.setItem('dashboard_lastSeenCustomLeads', String(siteActivity.totalCustomLeads));
    setSiteActivity(prev => ({ ...prev, newReservations: 0, newCustomLeads: 0 }));
  };

  const handleAddReminder = async () => {
    if (!newReminder.operation_id || !newReminder.title) {
      alert('Selecione um pacote e informe o título do lembrete');
      return;
    }
    
    try {
      if (editingReminder) {
        // Modo edição
        await axios.put(`${BACKEND_URL}/api/operational-results/reminders/${editingReminder.id}`, {
          ...newReminder,
          completed: editingReminder.completed
        });
      } else {
        // Modo criação
        await axios.post(`${BACKEND_URL}/api/operational-results/reminders`, newReminder);
      }
      setShowReminderModal(false);
      setEditingReminder(null);
      setNewReminder({ operation_id: '', title: '', description: '', due_date: '', priority: 'normal' });
      loadPendingReminders();
    } catch (error) {
      console.error('Erro ao salvar lembrete:', error);
      alert('Erro ao salvar lembrete');
    }
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder(reminder);
    setNewReminder({
      operation_id: reminder.operation_id || '',
      title: reminder.title || '',
      description: reminder.description || '',
      due_date: reminder.due_date || '',
      priority: reminder.priority || 'normal'
    });
    setShowReminderModal(true);
  };

  const handleToggleReminder = async (reminder) => {
    try {
      await axios.put(`${BACKEND_URL}/api/operational-results/reminders/${reminder.id}`, {
        completed: !reminder.completed
      });
      loadPendingReminders();
    } catch (error) {
      console.error('Erro ao atualizar lembrete:', error);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Excluir este lembrete?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/operational-results/reminders/${reminderId}`);
      loadPendingReminders();
    } catch (error) {
      console.error('Erro ao excluir lembrete:', error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  const menuSections = [
    {
      key: 'operacional',
      title: 'Operacional / Financeiro',
      color: 'text-[#003366]',
      items: [
        { to: '/admin/pacotes', icon: List, iconBg: 'bg-[#003366]', title: 'Gerenciar Pacotes', description: 'Visualizar, editar e excluir pacotes' },
        { to: '/admin/financeiro', icon: BarChart3, iconBg: 'bg-emerald-600', title: 'Painel Financeiro', description: 'Gráficos de vendas e receita' },
        { to: '/admin/dre', icon: PieChart, iconBg: 'bg-indigo-600', title: 'DRE Completo', description: 'Receitas, Despesas e Ranking' },
        { to: '/admin/resultados-operacionais', icon: PieChart, iconBg: 'bg-violet-600', title: 'Resultados Operacionais', description: 'DRE por pacote e data' },
        { to: '/admin/fluxo-caixa', icon: Wallet, iconBg: 'bg-green-600', title: 'Fluxo de Caixa', description: 'Receitas ASAAS/STONE e despesas' },
        { to: '/admin/asaas', icon: DollarSign, iconBg: 'bg-blue-600', title: 'Integração ASAAS', description: 'Boletos, cartões e sincronização' },
        { to: '/admin/despesas-gerais', icon: DollarSign, iconBg: 'bg-red-600', title: 'Despesas Gerais', description: 'Site, luz, uniformes, marketing' },
      ]
    },
    {
      key: 'clientes',
      title: 'Clientes',
      color: 'text-green-700',
      items: [
        { to: '/admin/reservas', icon: Users, iconBg: 'bg-green-600', title: 'Reservas / Clientes', description: 'Ver reservas e dados dos clientes' },
        { to: '/admin/clientes', icon: Users, iconBg: 'bg-orange-600', title: 'Gerenciar Clientes', description: 'Cadastros e verificação de e-mail' },
        { to: '/admin/reservas-manuais', icon: UserPlus, iconBg: 'bg-cyan-600', title: 'Reservas Manuais', description: 'Clientes reservados fora do site' },
        { to: '/admin/passageiros', icon: Users, iconBg: 'bg-cyan-700', title: 'Lista de Passageiros', description: 'Todos os viajantes por pacote/data' },
        { to: '/admin/bolsas-credito', icon: Wallet, iconBg: 'bg-teal-600', title: 'Bolsas de Crédito', description: 'Saldos Normal e Limitada por CPF' },
        { to: '/admin/solicitacoes-cancelamento', icon: AlertCircle, iconBg: 'bg-orange-600', title: 'Solicitações de Cancelamento', description: 'Fila de análise das solicitações dos clientes' },
        { to: '/admin/documentos-menores', icon: FileText, iconBg: 'bg-purple-700', title: 'Documentos de Menores', description: 'Fila de análise dos documentos de menores' },
      ]
    },
    {
      key: 'parceiros',
      title: 'Parceiros',
      color: 'text-pink-700',
      items: [
        { to: '/admin/comissoes', icon: Gift, iconBg: 'bg-pink-600', title: 'Comissões', description: 'Clientes com cupom de desconto' },
        { to: '/admin/pagamentos-comissao', icon: DollarSign, iconBg: 'bg-emerald-700', title: 'Pagamentos de Comissão', description: 'Criar pagamento PIX e anexar comprovante' },
        { to: '/admin/cupons', icon: Gift, iconBg: 'bg-purple-600', title: 'Cupons de Desconto', description: 'Criar e gerenciar cupons' },
      ]
    },
    {
      key: 'ajustes',
      title: 'Ajustes Gerais',
      color: 'text-amber-700',
      items: [
        { to: '/admin/imagens', icon: Image, iconBg: 'bg-purple-600', title: 'Biblioteca de Imagens', description: 'Gerenciar imagens do site' },
        { to: '/admin/pagina/inicio', icon: Home, iconBg: 'bg-blue-500', title: 'Editar Página Início', description: 'Textos e conteúdo da Home' },
        { to: '/admin/pagina/sobre', icon: Info, iconBg: 'bg-teal-600', title: 'Editar Sobre Nós', description: 'Textos da página Sobre' },
        { to: '/admin/pagina/contato', icon: Phone, iconBg: 'bg-indigo-600', title: 'Editar Contato', description: 'Informações de contato' },
        { to: '/admin/pagina/termo-adesao', icon: FileText, iconBg: 'bg-purple-600', title: 'Termo de Adesão', description: 'Política de cancelamento e termos' },
        { to: '/admin/faq', icon: HelpCircle, iconBg: 'bg-amber-500', title: 'Perguntas Frequentes', description: 'Gerenciar FAQ do site' },
        { to: '/admin/contratos', icon: FileText, iconBg: 'bg-red-600', title: 'Contratos', description: 'Modelos de contrato por categoria' },
        { to: '/admin/configuracoes', icon: Settings, iconBg: 'bg-gray-600', title: 'Configurações', description: 'Pontos de embarque e opções' },
      ]
    },
    {
      key: 'contatos',
      title: 'Contatos',
      color: 'text-pink-700',
      items: [
        { to: '/admin/emails', icon: Mail, iconBg: 'bg-pink-600', title: 'E-mails', description: 'Caixa de entrada e envios' },
        { to: '/admin/lead-forms', icon: FileText, iconBg: 'bg-orange-500', title: 'Formulários de Captação', description: 'Lead forms estilo Typeform' },
        { to: '/admin/pacotes-personalizados', icon: FileText, iconBg: 'bg-blue-500', title: 'Pacotes Personalizados', description: 'Editar página e ver leads' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_419069bb-d46c-413f-9dd8-09d0cbbf3735/artifacts/rwf0qmt2_LOGO.png" 
                alt="TÔ INDO ALI" 
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold">Painel Administrativo</h1>
                <p className="text-sm text-gray-300 hidden sm:block">TÔ INDO ALI - Turismo e Aventura</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4">
              <Button onClick={handleLogout} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
              <a href="/" target="_blank" className="text-sm hover:text-[#FFD700] whitespace-nowrap">
                Ver Site →
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total de Pacotes</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalPackages}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pacotes Ativos</p>
                  <p className="text-3xl font-bold text-green-600">{stats.activePackages}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total de Datas</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalDates}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Preço Médio</p>
                  <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.averagePrice)}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Lembretes: Preencher Resultado Operacional (pós-viagem) */}
        {postTripReminders.length > 0 && (
          <div className="mb-8">
            <Card className="border-blue-300 bg-blue-50" data-testid="post-trip-reminders-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">Preencher Resultado Operacional</h2>
                    <p className="text-sm text-blue-700">{postTripReminders.length} viagem(ns) já terminaram e aguardam preenchimento do resultado</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {postTripReminders.map(r => (
                    <div key={r.operation_id} className="bg-white rounded-lg p-4 border border-blue-200 flex items-start justify-between gap-3" data-testid={`post-trip-${r.operation_id}`}>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{r.package_name}</p>
                        <p className="text-xs text-gray-600">{r.date_display} · terminou há {r.days_since_end} dia(s) · {r.reservations_count} reserva(s)</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/admin/resultados-operacionais?operation=${r.operation_id}`)} data-testid={`open-post-trip-${r.operation_id}`}>
                          Preencher
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => markPostTripDone(r.operation_id)} data-testid={`done-post-trip-${r.operation_id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Concluído
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alertas de Despesas Pendentes */}
        {!alertsLoading && pendingAlerts.length > 0 && (
          <div className="mb-8">
            <Card className="border-orange-300 bg-orange-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-orange-800">⚠️ Despesas Pendentes de Pagamento</h2>
                    <p className="text-sm text-orange-600">{pendingAlerts.length} viagem(ns) com despesas projetadas próximas da data</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {pendingAlerts.map((alert) => (
                    <div 
                      key={alert.operation_id} 
                      className="bg-white rounded-lg p-4 border border-orange-200 hover:border-orange-400 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              alert.days_until <= 7 ? 'bg-red-100 text-red-800' :
                              alert.days_until <= 20 ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              <Clock className="w-3 h-3 inline mr-1" />
                              {alert.days_until === 0 ? 'HOJE!' : 
                               alert.days_until === 1 ? 'Amanhã' : 
                               `${alert.days_until} dias`}
                            </span>
                            <span className="text-xs text-gray-500">{alert.category_label}</span>
                          </div>
                          
                          <h3 className="font-bold text-gray-900">{alert.package_name}</h3>
                          <p className="text-sm text-gray-600">{alert.date_display}</p>
                          
                          <div className="mt-2 flex flex-wrap gap-2">
                            {Object.entries(alert.pending_expenses).map(([category, value]) => (
                              <span key={category} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                {category}: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="text-right ml-4">
                          <p className="text-xs text-gray-500">Total Pendente</p>
                          <p className="text-lg font-bold text-orange-600">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(alert.total_pending)}
                          </p>
                          <Link 
                            to={`/admin/resultados-operacionais?pkg=${alert.package_id}&date=${alert.date_id}`}
                            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 mt-1"
                          >
                            Ver detalhes <ChevronRight className="w-3 h-3 ml-1" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alertas de Disponibilidade de Pacotes - Só mostra se houver vagas para vender */}
        {availabilityAlerts.filter(a => a.spots_available > 0).length > 0 && (
          <div className="mb-8">
            <Card className="border-purple-300 bg-purple-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-purple-800">📅 Pacotes que Ficarão Indisponíveis</h2>
                      <p className="text-sm text-purple-600">
                        {availabilityAlerts.filter(a => a.spots_available > 0).length} data(s) próxima(s) do prazo mínimo de vendas
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandAvailability(!expandAvailability)}
                    className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                  >
                    {expandAvailability ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    <span className="ml-1 text-xs">{expandAvailability ? 'Minimizar' : 'Expandir'}</span>
                  </Button>
                </div>
                
                {expandAvailability && (
                  <div className="space-y-3">
                    {availabilityAlerts.filter(a => a.spots_available > 0).map((alert, index) => (
                      <Link 
                        key={index}
                        to={`/admin/pacotes/editar/${alert.package_id}`}
                        className={`block bg-white rounded-lg p-4 border hover:shadow-md transition-shadow cursor-pointer ${alert.urgent ? 'border-red-400 hover:border-red-500' : 'border-purple-200 hover:border-purple-400'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-bold text-blue-600 hover:text-blue-800">
                              {alert.package_name}
                            </span>
                            <p className="text-sm text-gray-600">{alert.date_display}</p>
                            <p className="text-xs text-green-600 mt-1">{alert.spots_available} vaga(s) disponível(is)</p>
                          </div>
                          <div className="text-right">
                            {alert.urgent ? (
                              <span className="inline-block px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded">
                                ⚠️ JÁ DEVERIA ESTAR OCULTO
                              </span>
                            ) : (
                              <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded">
                                {alert.days_until_unavailable} dia(s) para ficar indisponível
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Faltam {alert.days_until_trip} dias para a viagem • 
                          Prazo mínimo: {alert.min_days_required} dias ({alert.category === 'rodoviario' ? 'Rodoviário' : 'Aéreo/Internacional'})
                        </p>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Atividade do Site (novas reservas + leads de pacotes personalizados) */}
        {(siteActivity.newReservations > 0 || siteActivity.newCustomLeads > 0) && (
          <div className="mb-8">
            <Card className="border-green-300 bg-green-50">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-green-800">🔔 Novidades do Site</h2>
                      <p className="text-sm text-green-600">
                        Desde sua última visita ao dashboard
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markActivityAsSeen}
                    className="text-green-700 hover:bg-green-100"
                    data-testid="mark-activity-as-seen"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Marcar como visto
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Reservas novas */}
                  {siteActivity.newReservations > 0 && (
                    <Link
                      to="/admin/reservas"
                      className="bg-white rounded-lg p-4 border border-green-200 hover:shadow-md transition-shadow cursor-pointer"
                      data-testid="new-reservations-card"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">
                            +{siteActivity.newReservations} {siteActivity.newReservations === 1 ? 'nova reserva' : 'novas reservas'} pelo site
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Total atual: {siteActivity.totalReservations} reserva(s)
                          </p>
                          {siteActivity.recentReservations.slice(0, 3).map((r, i) => (
                            <p key={r.id || i} className="text-xs text-gray-600 mt-1 truncate">
                              • {r.customer_name || 'Cliente'}
                              {r.package_name ? ` — ${r.package_name}` : ''}
                            </p>
                          ))}
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Link>
                  )}

                  {/* Leads de Pacotes Personalizados */}
                  {siteActivity.newCustomLeads > 0 && (
                    <Link
                      to="/admin/pacotes-personalizados"
                      className="bg-white rounded-lg p-4 border border-blue-200 hover:shadow-md transition-shadow cursor-pointer"
                      data-testid="new-custom-leads-card"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <UserPlus className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">
                            +{siteActivity.newCustomLeads} {siteActivity.newCustomLeads === 1 ? 'solicitação' : 'solicitações'} de Pacote Personalizado
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Total atual: {siteActivity.totalCustomLeads} solicitação(ões)
                          </p>
                          {siteActivity.recentCustomLeads.slice(0, 3).map((lead, i) => {
                            const name = lead.answers?.find(a => a.question_id === 'name')?.answer || 'Sem nome';
                            const destination = lead.answers?.find(a => a.question_id === 'destination')?.answer;
                            return (
                              <p key={lead.id || i} className="text-xs text-gray-600 mt-1 truncate">
                                • {name}{destination ? ` — ${destination}` : ''}
                              </p>
                            );
                          })}
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Lembretes Pendentes */}
        <div className="mb-8">
          <Card className="border-blue-300 bg-blue-50">
            <CardContent className="p-4 md:p-6">
              {/* Header com botão no canto superior direito */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-blue-800">📝 Lembretes</h2>
                    <p className="text-sm text-blue-600">
                      {pendingReminders.length} lembrete(s) pendente(s)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.post(`${BACKEND_URL}/api/operational-results/generate-past-trip-reminders`);
                        if (response.data.success) {
                          if (response.data.created_count > 0) {
                            alert(response.data.message);
                            loadPendingReminders();
                          } else {
                            alert('Nenhuma viagem concluída sem lembrete encontrada.');
                          }
                        }
                      } catch (error) {
                        console.error('Erro ao gerar lembretes:', error);
                      }
                    }}
                    className="text-orange-600 hover:text-orange-800 hover:bg-orange-100 text-xs"
                    title="Gerar lembretes para viagens que já ocorreram"
                  >
                    🔄 Viagens Passadas
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandReminders(!expandReminders)}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
                  >
                    {expandReminders ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    <span className="ml-1 text-xs hidden sm:inline">{expandReminders ? 'Minimizar' : 'Expandir'}</span>
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => setShowReminderModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 md:mr-1" />
                    <span className="hidden md:inline">Novo Lembrete</span>
                  </Button>
                </div>
              </div>
              
              {expandReminders && (
                <>
                  {remindersLoading ? (
                    <div className="text-center py-4 text-gray-500">Carregando...</div>
                  ) : pendingReminders.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      Nenhum lembrete pendente. Clique no botão + para adicionar.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingReminders.map((reminder) => {
                        // Calcular dias restantes para o prazo
                        const dueDate = reminder.due_date ? new Date(reminder.due_date + 'T12:00:00') : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const daysUntilDue = dueDate ? Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24)) : null;
                        const isUrgent = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;
                        const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
                        
                        return (
                        <div 
                          key={reminder.id} 
                          className={`bg-white rounded-lg p-3 border hover:shadow-md transition-shadow ${
                            reminder.priority === 'high' ? 'border-red-300 hover:border-red-400' :
                            reminder.priority === 'low' ? 'border-gray-200 hover:border-gray-400' :
                            'border-blue-200 hover:border-blue-400'
                          }`}
                        >
                          {/* Prazo em linha única no topo - com animação piscando se urgente */}
                          {reminder.due_date && (
                            <div className={`text-xs font-semibold mb-2 ${
                              isOverdue ? 'text-red-600' : 
                              isUrgent ? 'text-orange-600 animate-pulse' : 
                              'text-gray-500'
                            }`}>
                              ⏰ Prazo: {dueDate.toLocaleDateString('pt-BR')}
                              {isOverdue && ' (ATRASADO!)'}
                              {isUrgent && !isOverdue && ` (${daysUntilDue} dia${daysUntilDue !== 1 ? 's' : ''} restante${daysUntilDue !== 1 ? 's' : ''})`}
                            </div>
                          )}
                          
                          <div className="flex items-start gap-3">
                            <button
                              onClick={() => handleToggleReminder(reminder)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                reminder.completed 
                                  ? 'bg-green-500 border-green-500 text-white' 
                                  : 'border-gray-300 hover:border-blue-500'
                              }`}
                            >
                              {reminder.completed && <CheckCircle className="w-4 h-4" />}
                            </button>
                            
                            {/* Área principal clicável - Link para edição do pacote */}
                            <Link 
                              to={reminder.package_id ? `/admin/pacotes/editar/${reminder.package_id}` : '#'}
                              className="flex-1 min-w-0 cursor-pointer"
                            >
                              {/* Prioridade e Título */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {reminder.priority === 'high' && (
                                  <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded font-medium">Alta</span>
                                )}
                                <span className={`font-medium ${reminder.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  {reminder.title}
                                </span>
                              </div>
                              
                              {/* Destino e Data */}
                              <div className="text-xs text-gray-500 mt-1">
                                <span className="font-medium text-blue-600 hover:text-blue-800">
                                  {reminder.package_name}
                                </span>
                                {reminder.date_display && (
                                  <span> • {reminder.date_display}</span>
                                )}
                              </div>
                              
                              {/* Descrição */}
                              {reminder.description && (
                                <p className="text-xs text-gray-500 mt-1">{reminder.description}</p>
                              )}
                            </Link>
                            
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleEditReminder(reminder)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteReminder(reminder.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                                title="Excluir"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - Agrupado por seção */}
        <div className="space-y-8">
          {menuSections.map((section) => (
            <div key={section.key}>
              <h2 className={`text-lg font-bold uppercase tracking-wide mb-3 ${section.color}`}>
                {section.title}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {section.items.map((item, index) => (
                  <Link key={`${section.key}-${index}`} to={item.to}>
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                          <div className={`w-12 h-12 md:w-14 md:h-14 ${item.iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <item.icon className={`w-6 h-6 md:w-7 md:h-7 ${item.iconColor || 'text-white'}`} />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 text-sm md:text-base">{item.title}</h3>
                            <p className="text-xs md:text-sm text-gray-600 hidden sm:block">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Novo/Editar Lembrete */}
      <Dialog open={showReminderModal} onOpenChange={(open) => {
        if (!open) {
          setEditingReminder(null);
          setNewReminder({ operation_id: '', title: '', description: '', due_date: '', priority: 'normal' });
        }
        setShowReminderModal(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>📝 {editingReminder ? 'Editar Lembrete' : 'Novo Lembrete'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Pacote / Viagem *</Label>
              <select
                value={newReminder.operation_id}
                onChange={(e) => setNewReminder({...newReminder, operation_id: e.target.value})}
                className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione um pacote...</option>
                {operations.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.package_name} - {op.date}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Título *</Label>
              <Input
                value={newReminder.title}
                onChange={(e) => setNewReminder({...newReminder, title: e.target.value})}
                placeholder="Ex: Reservar ingressos, Confirmar hospedagem..."
              />
            </div>
            
            <div>
              <Label>Descrição (opcional)</Label>
              <Input
                value={newReminder.description}
                onChange={(e) => setNewReminder({...newReminder, description: e.target.value})}
                placeholder="Detalhes adicionais..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prazo (opcional)</Label>
                <Input
                  type="date"
                  value={newReminder.due_date}
                  onChange={(e) => setNewReminder({...newReminder, due_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Prioridade</Label>
                <select
                  value={newReminder.priority}
                  onChange={(e) => setNewReminder({...newReminder, priority: e.target.value})}
                  className="w-full mt-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowReminderModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddReminder}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Criar Lembrete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
